import { useMemo, useState } from 'react';
import cn from 'classnames';
import { MdAutoAwesome, MdLayers } from 'react-icons/md';
import { InteractiveDiv } from '../../../Components/InteractiveEl';
import { SegmentedControlButton } from '../../../Components/SegmentedControlButton';
import { Size } from '../../../Components/designConstants';
import { AlexaAssessmentStore, summarizeAlexaReadiness } from './alexa-assessments-utils';
import { DeficienciesPanel } from './DeficienciesPanel';
import { DEFICIENCIES_LIST, computeDeficiencySummary } from './deficiencies-constants';
import { ProductScoreTable } from './ProductScoreTable';
import { ProposalStore } from './proposals-utils';
import { ScorecardKpiCards } from './ScorecardKpiCards';
import { ScoreMode, ScoreModeType } from './scorecard-constants';
import { computePortfolioKpis } from './scorecard-utils';
import { AuditedProduct } from './scoring-engine-constants';

const SCORE_MODE_OPTIONS = [
  {
    key: ScoreMode.HEALTH,
    label: 'Structural Health',
    renderLabel: (v: string) => (
      <>
        <MdLayers /> {v}
      </>
    )
  },
  {
    key: ScoreMode.ALEXA,
    label: 'Alexa Readiness',
    renderLabel: (v: string) => (
      <>
        <MdAutoAwesome /> {v}
      </>
    )
  }
] as const;

interface ScorecardHealthWorkspaceProps {
  products: AuditedProduct[];
  proposals: ProposalStore;
  assessments: AlexaAssessmentStore;
  tld: string;
  onOptimize: (product: AuditedProduct) => void;
  onAssess: (product: AuditedProduct) => void;
}

export const ScorecardHealthWorkspace = ({
  products,
  proposals,
  assessments,
  tld,
  onOptimize,
  onAssess
}: ScorecardHealthWorkspaceProps) => {
  const [scoreMode, setScoreMode] = useState<ScoreModeType>(ScoreMode.HEALTH);
  const [activeDefectFilterId, setActiveDefectFilterId] = useState<string | null>(null);

  const kpis = useMemo(() => computePortfolioKpis(products), [products]);
  const alexaReadiness = useMemo(
    () => summarizeAlexaReadiness(products, assessments),
    [products, assessments]
  );

  const activeDeficiency = useMemo(
    () => DEFICIENCIES_LIST.find(d => d.id === activeDefectFilterId) ?? null,
    [activeDefectFilterId]
  );
  // Flagged-bottleneck counts feeding the table filter chips (shared source with the panel)
  const deficiencySummary = useMemo(() => computeDeficiencySummary(products), [products]);
  const filteredProducts = useMemo(
    () => (activeDeficiency ? products.filter(activeDeficiency.countPredicate) : products),
    [products, activeDeficiency]
  );

  return (
    <div className='scorecard-health-workspace'>
      <ScorecardKpiCards
        kpis={kpis}
        scoreMode={scoreMode}
        alexaAverage={alexaReadiness.averageScore}
      />

      <div className='scorecard-mode-toggle-row'>
        <div className='scorecard-mode-toggle-group'>
          <span className='scorecard-mode-eyebrow'>Score View</span>
          <SegmentedControlButton
            size={Size.SM}
            selectedOption={scoreMode}
            onChange={mode => {
              setScoreMode(mode);
              // The filter chips only render in Health mode — clear the filter on
              // switch so Alexa mode never shows an invisibly-filtered table.
              setActiveDefectFilterId(null);
            }}
            options={SCORE_MODE_OPTIONS}
          />
        </div>
        <p className='scorecard-mode-caption'>
          {scoreMode === ScoreMode.HEALTH
            ? 'Structural compliance — computed instantly for every listing, no AI cost.'
            : 'AI-assessed query discoverability — opt-in per listing; only what you assess spends tokens.'}
        </p>
      </div>

      {scoreMode === ScoreMode.HEALTH ? (
        <DeficienciesPanel
          products={products}
          activeFilterId={activeDefectFilterId}
          onSelectFilter={setActiveDefectFilterId}
        />
      ) : (
        <div className='scorecard-alexa-summary'>
          <div className='scorecard-alexa-summary-headline'>
            <span className='scorecard-alexa-summary-label'>AI-Assessed Readiness</span>
            <span className='scorecard-alexa-summary-value'>
              {alexaReadiness.averageScore !== null ? `${alexaReadiness.averageScore}%` : '—'}
            </span>
            <span className='scorecard-alexa-summary-sub'>
              {alexaReadiness.assessedCount} of {alexaReadiness.totalCount} listings assessed
            </span>
          </div>
          {alexaReadiness.dimensionAverages.length > 0 && (
            <div className='scorecard-alexa-summary-dimensions'>
              {alexaReadiness.dimensionAverages.map(d => (
                <div key={d.key} className='scorecard-alexa-dimension'>
                  <div className='scorecard-alexa-dimension-header'>
                    <span>{d.label}</span>
                    <span>{d.average}/25</span>
                  </div>
                  <div className='score-meter'>
                    <div
                      className='score-meter-fill'
                      style={{ width: `${(d.average / 25) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className='product-score-table-section'>
        <div className='product-score-table-section-header'>
          <h2>Scored Listings</h2>
        </div>
        {scoreMode === ScoreMode.HEALTH && deficiencySummary.flagged.length > 0 && (
          <div
            className='listings-filter-bar'
            role='group'
            aria-label='Filter listings by bottleneck'
          >
            <InteractiveDiv
              className={cn('listings-filter-chip', { 'is-active': activeDefectFilterId === null })}
              aria-pressed={activeDefectFilterId === null}
              onClick={() => setActiveDefectFilterId(null)}
            >
              All listings
              <span className='listings-filter-chip-count'>{products.length}</span>
            </InteractiveDiv>
            {deficiencySummary.flagged.map(({ filter, count }) => {
              const isActive = activeDefectFilterId === filter.id;
              return (
                <InteractiveDiv
                  key={filter.id}
                  className={cn('listings-filter-chip', { 'is-active': isActive })}
                  aria-pressed={isActive}
                  onClick={() => setActiveDefectFilterId(isActive ? null : filter.id)}
                >
                  {filter.shortLabel}
                  <span className='listings-filter-chip-count'>{count}</span>
                </InteractiveDiv>
              );
            })}
          </div>
        )}
        <ProductScoreTable
          products={filteredProducts}
          activeFilterId={activeDefectFilterId}
          scoreMode={scoreMode}
          proposals={proposals}
          assessments={assessments}
          tld={tld}
          onOptimize={onOptimize}
          onAssess={onAssess}
        />
      </div>
    </div>
  );
};
